import { TPhoneStorage } from "./types"

type LibphonenumberModule = typeof import("libphonenumber-js/max")

let lib: LibphonenumberModule | null = null
let carregando: Promise<void> | null = null

/**
 * Carrega a biblioteca de numeração sob demanda. Chamar na troca de país
 * ou quando a detecção de locale apontar um país não-BR. Chamadas
 * concorrentes compartilham a mesma promise.
 *
 * Usa a variante /max, não /min: medido que /min não carrega os padrões
 * de validação por país (isValidPhoneNumber vira equivalente a checar só
 * comprimento — mesmo resultado de isPossible). Como a premissa do design
 * é que a validação real de qualidade de dado acontece aqui no frontend
 * (o backend só rejeita número estruturalmente impossível), a variante
 * precisa validar de verdade. BR nunca carrega nenhuma variante — o custo
 * extra de /max (~20KB gzip a mais que /min) só é pago por quem já
 * precisa da validação completa.
 */
export const ensureEngine = async (): Promise<void> => {
  if (lib) return
  if (!carregando) {
    carregando = import("libphonenumber-js/max").then((mod) => {
      lib = mod
    })
  }
  await carregando
}

/** Exposto para teste — indica se a biblioteca já está em memória. */
export const isEngineLoaded = (): boolean => lib !== null

/**
 * AC, BO, FK, GI, KI, SH, TA, TK não têm regra de formatação na
 * metadata do libphonenumber — AsYouType devolve os dígitos crus para
 * esses territórios. Só a Bolívia tem volume comercial relevante para
 * justificar uma máscara manual; os demais ficam sem formatação.
 */
const MASCARAS_MANUAIS: Record<string, string> = {
  BO: "#### ####",
}

const aplicarManual = (digits: string, pattern: string): string => {
  let i = 0
  return pattern
    .split("")
    .map((c) => (c === "#" ? digits[i++] ?? "" : c))
    .join("")
    .replace(/\s+$/, "")
}

/**
 * Máscara progressiva. Síncrona de propósito: react-hook-form usa
 * transform síncrono a cada tecla. Enquanto a biblioteca não carregou,
 * devolve os dígitos crus em vez de quebrar o input.
 */
export const formatIntl = (value: string, iso: string): string => {
  const digits = value.replace(/\D/g, "")
  if (!digits) return ""

  const manual = MASCARAS_MANUAIS[iso]
  if (manual) return aplicarManual(digits, manual)

  if (!lib) return digits
  return new lib.AsYouType(iso as never).input(digits)
}

export const isValidIntl = (value: string, iso: string): boolean => {
  if (!lib) return false
  return lib.isValidPhoneNumber(value, iso as never)
}

/**
 * Converte o número no formato local digitado pelo usuário para o
 * nacional significativo do E.164 — removendo prefixo nacional e
 * aplicando as transformações específicas do país.
 */
export const toStorageIntl = (value: string, iso: string): TPhoneStorage => {
  if (!lib) return { phone: value.replace(/\D/g, ""), ddi: "", iso }
  const parsed = lib.parsePhoneNumberFromString(value, iso as never)
  return {
    phone: parsed ? parsed.nationalNumber.toString() : "",
    ddi: lib.getCountryCallingCode(iso as never),
    iso,
  }
}
